import React, { useState, useEffect } from 'react';

import { IVideoModuleFields } from '@/types/contentful';

import { useUserContext } from '../../context/User';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getPartnerTrackingProperties } from '../../utils/getPartnerTrackingProperties';
import { captureError, trackRumEvent } from '../../utils/datadogRum';

import { Lightbox } from '../VideoBlock/Lightbox';

import { PlayIcon, VideoButton, VideoWrapper } from './styles';
import { useVideo } from '~/hooks/useVideo';

const VIDEO_ERRORS_TO_IGNORE = [
  'play() request was interrupted by a call to pause',
  'play() request was interrupted by a new load request'
];

interface VideoPlayerProps extends IVideoModuleFields {
  rounded?: boolean;
  fullwidth?: boolean;
  variant?: 'default' | 'gl4c';
  sysId?: string;
}

export function VideoPlayer({
  title,
  vimeoId,
  loop,
  mp4video,
  fallbackImage,
  rounded,
  fullwidth = true,
  variant,
  sysId
}: VideoPlayerProps) {
  const { educator } = useUserContext();
  const [showingVideo, setShowingVideo] = useState(false);
  const { videoRef, sourceUrl } = useVideo(mp4video?.fields?.file?.url, {
    manageVisibility: true,
    shouldPlay: true,
    intersectionThreshold: 0.25,
    deferUntilVisible: true
  });

  useEffect(() => {
    videoRef.current?.play()?.catch((err: DOMException) => {
      if (!VIDEO_ERRORS_TO_IGNORE.includes(err.message)) {
        trackRumEvent('autoplay_failed', {
          page_url: window.location.href,
          ...educator,
          ...getPartnerTrackingProperties()
        });
        captureError(err);
      }
    });
  }, []);

  const toggleVideo = (displayVideo: boolean) => {
    setShowingVideo(displayVideo);
    if (displayVideo) {
      trackWithFingerprint('Video Opened', {
        page_url: window.location.href,
        ...educator,
        ...getPartnerTrackingProperties()
      });
    } else {
      trackWithFingerprint('Video Closed', {
        page_url: window.location.href,
        ...educator,
        ...getPartnerTrackingProperties()
      });
      document.getElementById('gl-video-play-button')?.focus();
    }
  };

  const trackVideoPlay = () => {
    trackWithFingerprint('Video Playing', {
      page_url: window.location.href,
      video_title: title,
      ...educator,
      ...getPartnerTrackingProperties()
    });
  };

  return (
    <VideoWrapper
      fullwidth={fullwidth}
      rounded={rounded}
      variant={variant}
      data-blockid="videoModuleWrapper"
      id={sysId}
    >
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        loop={loop!}
        poster={fallbackImage?.fields?.file?.url}
        playsInline
        muted
        controls={false}
        preload="metadata"
        crossOrigin="anonymous"
        onPlay={trackVideoPlay}
      >
        <track kind="captions" />
        <source src={sourceUrl} />
        Your browser does not support HTML video.
      </video>
      <VideoButton
        id="gl-video-play-button"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          toggleVideo(true);
          e.currentTarget.blur();
        }}
        aria-label="Play video"
        data-testid="videomodule-button"
      >
        <PlayIcon
          width="80"
          height="80"
          viewBox="0 0 100 100"
          role="img"
          data-testid="assetWith5050Text-video"
        >
          <path
            opacity={0.8}
            d="M0 24C0 10.745 10.745 0 24 0h32c13.255 0 24 10.745 24 24v32c0 13.255-10.745 24-24 24H24C10.745 80 0 69.255 0 56V24z"
            fill="#1D252C"
          />
          <path
            d="M49.666 37.935c1.333.77 1.333 2.694 0 3.464l-13.5 7.794c-1.333.77-3-.192-3-1.732V31.873c0-1.54 1.667-2.502 3-1.733l13.5 7.795z"
            fill="#3BBFAD"
          />
        </PlayIcon>
      </VideoButton>

      {vimeoId && title && (
        <Lightbox
          title={title}
          showingVideo={showingVideo}
          setShowingVideo={toggleVideo}
          vimeoId={vimeoId}
        />
      )}
    </VideoWrapper>
  );
}
